/**
 * CommitCompletionPanel — post-commit confirmation (Bill smoke-test round 2,
 * 2026-07-10: "FSR left high and dry after upload").
 *
 * Rendered by CommitFlow once the pipeline reaches `committed`. Shows what the
 * pipeline ACTUALLY did, from the server's completion confirmation record
 * (GET /commit/status → completionSummary, persisted at commit finalize by
 * PipelineResumeFunction.PersistCompletionRecordAsync):
 *
 *   - Accounts opened   — FULL account number + product name + Core type
 *   - Customer records  — CIFs created vs updated
 *   - Documents signed  — distinct signature-captured docs (consent excluded)
 *   - Committed at      — bank-local timestamp
 *   - Card issuance     — CardIssuanceSection: SPFx CardIssuancePanel port
 *                         (Collette Cruse 3/27/2026 workflow, Bill
 *                         2026-07-10) — print in Entrust, then Activate
 *                         (EFTCardMod). Activation auto-completes the
 *                         'debit-card-print' follow-up task server-side,
 *                         so the follow-ups list refreshes afterwards.
 *   - Customer copy     — BACKLOG-1298: download the customer's PDF (signed
 *                         documents + disclosures) to print, then record it
 *                         handed over or mailed ('disclosure-copies' task)
 *   - Follow-ups        — REAL open follow-up tasks for the primary CIF
 *                         (/v3/follow-up-tasks/by-cif — the Wave-12 store),
 *                         plus static next-step reminders (plain text — the
 *                         pipeline does not auto-create these, so no buttons)
 *   - Actions           — Download for Aurora (existing zip endpoint) +
 *                         Back to applications
 *
 * Account numbers render FULL per the standing rule — the FSR files them in
 * Aurora; masking is for logs/assistant output, never the FSR record surface.
 */

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import type {
  CartCompletionSummary,
  CartDto,
  FollowUpTask,
} from '@retail-ops/shared-types';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApi } from './hooks/useApi';
import { useConfig } from '@/contexts/ConfigContext';
import { parsePeople } from './utils/cartStateParse';
import { CardIssuanceSection } from './CardIssuanceSection';
import { TestCleanupSection } from './TestCleanupSection';
import { RemoteSignatureActions, SignatureOutstandingActions } from '@/modules/FollowUpTasks/FollowUpTasksList';
import { followUpLabel, signatureStateKey, visibleFollowUps } from '@/utils/followUpTasks';
import { BANNO_URL_NOT_SET_MESSAGE, readBannoBackOfficeUrl } from '@/utils/bannoLink';
import { app } from '@microsoft/teams-js';

export interface CommitCompletionPanelProps {
  cart: CartDto;
  /** Completion record off the last /commit/status poll (may be absent on legacy carts). */
  summary?: CartCompletionSummary;
  /** "Back to applications" action — same exit the old bottom button used. */
  onExit?: () => void;
  /** Bill 2026-10-02 — re-open the in-branch ceremony for signers still outstanding. */
  onResumeSigning?: () => void;
}

/**
 * Static next-step reminders. These are NOT tracked tasks — the commit
 * pipeline does not create them — so they render as plain reminder text
 * (no fake buttons). Real tracked follow-ups (Wave-12 store) render above
 * them when any exist for the primary CIF.
 */
const NEXT_STEP_REMINDERS: ReadonlyArray<string> = [
  'Debit card printing — order/print the card if the customer requested one.',
  'Check orders — place the starter/check order if requested.',
  'File the signed package in Aurora (download below).',
];

/** Follow-up category for the customer's copy (server CustomerCopy.TaskCategory, BACKLOG-1298). */
const CUSTOMER_COPY_CATEGORY = 'disclosure-copies';

/** Completion reasons — must match server CustomerCopy.ReasonHanded / ReasonMailed. */
const COPY_HANDED = 'Customer copy handed to the customer in branch';
const COPY_MAILED = 'Customer copy mailed to the customer';

/** Saves a Blob through a temporary link (the Aurora download pattern that works in Teams). */
function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export function CommitCompletionPanel({
  cart,
  summary,
  onExit,
  onResumeSigning,
}: CommitCompletionPanelProps): JSX.Element {
  const { client } = useApi();
  const { config } = useConfig();

  // ── Aurora download (same endpoint CartDetail's documents tab uses) ─────
  const [auroraBusy, setAuroraBusy] = useState(false);
  const [auroraError, setAuroraError] = useState<string | null>(null);

  const handleAuroraDownload = useCallback(async (): Promise<void> => {
    if (auroraBusy) return;
    setAuroraBusy(true);
    setAuroraError(null);
    try {
      const blob = await client.retail.carts.auroraPackage(cart.cartId);
      saveBlob(blob, `aurora-${cart.cartId.slice(0, 8)}.zip`);
    } catch (err) {
      setAuroraError(
        err instanceof Error ? err.message : 'Could not build the Aurora package. Try again.',
      );
    } finally {
      setAuroraBusy(false);
    }
  }, [auroraBusy, client, cart.cartId]);

  // ── Real follow-up tasks for every CIF on the application (fail-soft) ──
  // Primary first. A joint owner's task (e.g. 'cip-core-entry', BACKLOG-0615)
  // is filed on that owner's CIF, so the primary alone would hide it.
  const primaryCif = summary?.cifs?.[0]
    ?? summary?.cifsCreated?.[0]
    ?? parsePeople(cart).find((p) => p.role === 'primary')?.cifId
    ?? parsePeople(cart)[0]?.cifId;
  const cifKey = Array.from(
    new Set(
      [primaryCif, ...(summary?.cifs ?? []), ...(summary?.cifsCreated ?? []),
        ...parsePeople(cart).map((p) => p.cifId)]
        .filter((c): c is string => !!c && c.trim() !== ''),
    ),
  ).join('|');
  const [followUps, setFollowUps] = useState<ReadonlyArray<FollowUpTask>>([]);

  // Refreshable so the card-issuance Activate (which auto-completes the
  // 'debit-card-print' task server-side) can re-pull the open list.
  const refreshFollowUps = useCallback(async (): Promise<void> => {
    if (!cifKey) return;
    const results = await Promise.allSettled([
      ...cifKey.split('|').map((c) => client.retail.followUpTasks.listByCif(c)),
      // BACKLOG-1673 — remote-signature tasks are keyed by CART, not CIF.
      client.retail.followUpTasks.list({ target: cart.cartId, category: 'remote-signature', top: 50 }),
      // Bill 2026-10-02 — signers with no signature and no remote link.
      client.retail.followUpTasks.list({ target: cart.cartId, category: 'signature-outstanding', top: 50 }),
    ]);
    const seen = new Set<string>();
    const open: FollowUpTask[] = [];
    for (const r of results) {
      // Fail-soft: the panel never blocks on the follow-up store.
      if (r.status !== 'fulfilled') continue;
      for (const t of r.value.tasks ?? []) {
        if ((t.status === 'open' || t.status === 'in-progress') && !seen.has(t.id)) {
          seen.add(t.id);
          open.push(t);
        }
      }
    }
    setFollowUps(open);
  }, [client, cifKey]);

  useEffect(() => {
    void refreshFollowUps();
  }, [refreshFollowUps]);

  // 666 test 2026-11-04: the server closes the "Signature outstanding" task when the signing session
  // completes, and the CHG-0138 poll brings the new counts here, so re-read the tasks when they change.
  const sigKey = signatureStateKey(summary);
  const lastSigKey = useRef<string>(sigKey);
  useEffect(() => {
    if (lastSigKey.current === sigKey) return;
    lastSigKey.current = sigKey;
    void refreshFollowUps();
  }, [sigKey, refreshFollowUps]);

  // CHG-0092 — opening the finished screen re-checks Core (fields 927 enrolled / 705 opted out)
  // for an open "Verify e-statement enrollment" task, so it closes as soon as Core shows the
  // outcome instead of waiting for the daily check. Once per task per visit; fail-soft.
  const eStatementChecked = useRef<Set<string>>(new Set());
  useEffect(() => {
    const open = followUps.filter((t) => t.taskCategory === 'estatement-verify' && !eStatementChecked.current.has(t.id));
    if (open.length === 0) return;
    open.forEach((t) => eStatementChecked.current.add(t.id));
    void (async () => {
      const bypassKey = (import.meta.env.VITE_ADMIN_BYPASS_KEY as string | undefined) ?? '';
      const results = await Promise.allSettled(open.map((t) => client.retail.followUpTasks.checkEStatement(bypassKey, t.id)));
      const closedAny = results.some((r) => r.status === 'fulfilled' && ['enrolled', 'opted-out', 'mixed'].includes(r.value.result ?? ''));
      if (closedAny) await refreshFollowUps();
    })();
  }, [followUps, client, refreshFollowUps]);

  // ── Customer copy (BACKLOG-1298) ──────────────────────────────────────────
  // Legacy in-branch procedure: the disclosures are printed and handed to the
  // customer. The officer downloads one PDF, prints it, then records how the
  // customer got it, which completes the 'disclosure-copies' task.
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [copyRecording, setCopyRecording] = useState(false);
  const copyTask = followUps.find((t) => t.taskCategory === CUSTOMER_COPY_CATEGORY);
  // The copy task has its own section; the generic list shows the rest.
  // Every signer captured: the signature tasks are done even if the list predates the server closing them.
  const otherFollowUps = visibleFollowUps(followUps, summary).filter((t) => t.taskCategory !== CUSTOMER_COPY_CATEGORY);
  // BACKLOG-1747 — one-tap completion for the Aurora and Banno tasks.
  const [doneBusy, setDoneBusy] = useState<string | null>(null);

  // Banno back office (MacKenzie Mott 2026-10-16; Bill: the URL is a setting that can be
  // corrected — links.bannoBackOfficeUrl, seed 415). Opened with the Teams SDK like the
  // Entrust link (CardIssuanceSection) — Teams desktop/iOS swallow target=_blank. When the
  // setting is empty the button explains where an administrator sets it.
  const bannoUrl = readBannoBackOfficeUrl(config.systemSettings);
  const [bannoNotice, setBannoNotice] = useState<boolean>(false);
  const openBanno = useCallback((): void => {
    if (!bannoUrl) { setBannoNotice(true); return; }
    setBannoNotice(false);
    try {
      const maybe = (app as unknown as { openLink?: (url: string) => Promise<void> }).openLink;
      if (typeof maybe === 'function') { void maybe(bannoUrl); return; }
      if (typeof window !== 'undefined') window.open(bannoUrl, '_blank', 'noopener,noreferrer');
    } catch {
      if (typeof window !== 'undefined') window.open(bannoUrl, '_blank', 'noopener,noreferrer');
    }
  }, [bannoUrl]);
  const markDone = useCallback(async (id: string, reason: string): Promise<void> => {
    setDoneBusy(id);
    try {
      const bypassKey = (import.meta.env.VITE_ADMIN_BYPASS_KEY as string | undefined) ?? '';
      await client.retail.followUpTasks.complete(bypassKey, id, { completionReason: reason });
      await refreshFollowUps();
    } catch { /* the row stays; the officer can retry */ } finally {
      setDoneBusy(null);
    }
  }, [client, refreshFollowUps]);
  // Bill 2026-10-02 — honest signature state from the completion record.
  const outstandingSigners = summary?.signersOutstanding ?? [];
  const outstandingCount = outstandingSigners.length;
  const signaturesRequired = typeof summary?.signaturesRequired === 'number' ? summary.signaturesRequired : 0;

  const handleCustomerCopy = useCallback(async (): Promise<void> => {
    if (copyBusy) return;
    setCopyBusy(true);
    setCopyError(null);
    try {
      const blob = await client.retail.carts.customerCopy(cart.cartId);
      saveBlob(blob, `customer-copy-${cart.cartId.slice(0, 8)}.pdf`);
    } catch (err) {
      setCopyError(
        err instanceof Error ? err.message : "Couldn't make the customer copy. Try again.",
      );
    } finally {
      setCopyBusy(false);
    }
  }, [copyBusy, client, cart.cartId]);

  const recordCopyDelivery = useCallback(async (reason: string): Promise<void> => {
    if (!copyTask || copyRecording) return;
    setCopyRecording(true);
    setCopyError(null);
    try {
      // Empty bypass key rides the Teams bearer token (role 'Officer').
      const bypassKey = (import.meta.env.VITE_ADMIN_BYPASS_KEY as string | undefined) ?? '';
      const resp = await client.retail.followUpTasks.complete(bypassKey, copyTask.id, {
        completionReason: reason,
      });
      if (!resp.success) {
        setCopyError(resp.error ?? "Couldn't record that. Try again.");
      }
      await refreshFollowUps();
    } catch (err) {
      setCopyError(err instanceof Error ? err.message : "Couldn't record that. Try again.");
    } finally {
      setCopyRecording(false);
    }
  }, [copyTask, copyRecording, client, refreshFollowUps]);

  // ── Card issuance (SPFx CardIssuancePanel port, Bill 2026-07-10) ────────
  // When the interactive card section has cards to show, the generic
  // "Debit card printing" static reminder below is redundant — hide it.
  const [issuedCardCount, setIssuedCardCount] = useState(0);

  // ── Derivations ──────────────────────────────────────────────────────────
  const accounts = summary?.accountsCreated ?? [];
  const cifsCreated = summary?.cifsCreated ?? [];
  const cifsUpdated = summary?.cifsUpdated ?? [];
  const cifsAll = summary?.cifs ?? [];
  const committedAtText = formatWhen(summary?.committedAt ?? cart.committedAt);

  const productName = (code?: string): string => {
    if (!code) return 'Account';
    const hit = config.products.find((p) => p.code === code);
    return hit?.name ?? code;
  };

  return (
    <section
      aria-labelledby="commit-completion-h"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-5)',
        padding: 'var(--sp-6)',
        background: 'var(--surface-card)',
        border: '1px solid var(--surface-border)',
        borderRadius: 'var(--r-lg)',
      }}
    >
      <h3
        id="commit-completion-h"
        style={{ margin: 0, fontSize: 15, color: 'var(--pcb-blue-dark)' }}
      >
        What happened
      </h3>

      {/* ── Accounts opened ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <SectionLabel text="Accounts opened" />
        {accounts.length === 0 ? (
          <MutedNote text="Account details aren't on file for this application — use the Aurora manifest for the definitive record." />
        ) : (
          accounts.map((a) => (
            <div
              key={a.acctId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-4)',
                padding: 'var(--sp-3) var(--sp-4)',
                border: '1px solid var(--surface-border)',
                borderRadius: 'var(--r-md)',
                fontSize: 13,
              }}
            >
              <span style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 600 }}>
                {a.acctId}
              </span>
              <span style={{ color: 'var(--ink-700)' }}>{productName(a.productCode)}</span>
              {a.acctType && <Pill variant="info">{humanizeAcctType(a.acctType)}</Pill>}
            </div>
          ))
        )}
      </div>

      {/* ── Customer records ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <SectionLabel text="Customer records" />
        {cifsCreated.length === 0 && cifsUpdated.length === 0 && cifsAll.length === 0 ? (
          <MutedNote text="No customer-record detail on file." />
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)', fontSize: 13 }}>
            {cifsCreated.map((c) => (
              <span key={`c-${c}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                <Pill variant="ok">New</Pill>
                <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>CIF {c}</span>
              </span>
            ))}
            {cifsUpdated.map((c) => (
              <span key={`u-${c}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                <Pill variant="info">Updated</Pill>
                <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>CIF {c}</span>
              </span>
            ))}
            {/* CIFs without a created/updated split (legacy record shape). */}
            {cifsCreated.length === 0 && cifsUpdated.length === 0
              && cifsAll.map((c) => (
                <span key={`a-${c}`} style={{ fontFamily: 'var(--font-mono, monospace)' }}>CIF {c}</span>
              ))}
          </div>
        )}
      </div>

      {/* ── POD beneficiaries (Bill 2026-07-13 Item 31) ── */}
      {(summary?.beneficiaries?.length ?? 0) > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <SectionLabel text="Beneficiaries" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', fontSize: 13 }}>
            {summary!.beneficiaries!.map((b, i) => (
              <div key={`${b.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
                <Pill variant={b.role === 'contingent' ? 'muted' : 'info'}>
                  {b.role === 'contingent' ? 'Contingent' : 'Primary'}
                </Pill>
                <span>{b.name}</span>
                {b.relationship && <span style={{ color: 'var(--ink-500)' }}>{b.relationship}</span>}
                {typeof b.percent === 'number' && (
                  <span style={{ color: 'var(--ink-500)' }}>{b.percent}%</span>
                )}
                {b.cifId && (
                  <span style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--ink-600)' }}>
                    CIF {b.cifId}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Services elected per account (Bill 2026-07-13 Item 31) ── */}
      {(summary?.services?.length ?? 0) > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <SectionLabel text="Services" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', fontSize: 13 }}>
            {summary!.services!.map((s, i) => (
              <div key={`${s.productCode}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>
                  {s.acctId ?? s.productCode ?? '—'}
                </span>
                {(s.services ?? []).map((code) => (
                  <Pill key={code} variant="muted">{humanizeServiceCode(code)}</Pill>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Signatures (Bill 2026-10-02) — the accounts open before everyone
          has signed; say so, and point at the follow-ups below. ── */}
      {outstandingCount > 0 && (
        <Banner tone="warn" title="Not everyone has signed yet">
          The accounts are open. {outstandingCount === 1 ? 'One signer has' : `${outstandingCount} signers have`} not
          signed{outstandingSigners.some((s) => s.remote) ? ' (some have a remote link out)' : ''}. Each one has a
          follow-up below — sign in branch now, or send a remote link. The customer copy and the Aurora package
          are available once everyone has signed.
        </Banner>
      )}

      {/* ── Signing + timestamp ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-7)', fontSize: 13 }}>
        {signaturesRequired > 0 && (
          <div>
            <SectionLabel text="Signatures" />
            <div style={{ marginTop: 'var(--sp-2)', color: outstandingCount > 0 ? 'var(--warn-text)' : 'var(--ink-700)', fontWeight: outstandingCount > 0 ? 600 : 400 }}>
              {summary?.signaturesCaptured ?? 0} of {signaturesRequired} signers done
              {outstandingCount > 0 ? ` · ${outstandingCount} outstanding` : ''}
            </div>
          </div>
        )}
        <div>
          <SectionLabel text="Documents signed" />
          <div style={{ marginTop: 'var(--sp-2)', color: 'var(--ink-700)' }}>
            {typeof summary?.docsSigned === 'number'
              ? `${summary.docsSigned} document${summary.docsSigned === 1 ? '' : 's'}`
              : '—'}
          </div>
        </div>
        <div>
          <SectionLabel text="Committed" />
          <div style={{ marginTop: 'var(--sp-2)', color: 'var(--ink-700)' }}>{committedAtText}</div>
        </div>
      </div>

      {/* ── Debit card issuance — SPFx CardIssuancePanel port (Collette
          Cruse 3/27/2026 workflow, Bill 2026-07-10). Renders nothing when
          the pipeline issued no cards. ── */}
      <CardIssuanceSection
        cartId={cart.cartId}
        onCardsLoaded={setIssuedCardCount}
        onActivated={() => { void refreshFollowUps(); }}
      />

      {/* ── Test account cleanup (Bill 2026-09-15). Renders nothing for a
          real application. ── */}
      <TestCleanupSection cartId={cart.cartId} />

      {/* ── Customer copy (BACKLOG-1298) ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <SectionLabel text="Customer copy" />
        <div style={{ fontSize: 13, color: 'var(--ink-700)' }}>
          Print the customer&apos;s copy of their signed documents and disclosures, then give it to
          them or mail it.
        </div>
        {copyError && <Banner tone="err">{copyError}</Banner>}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <Button variant="primary" onClick={() => { void handleCustomerCopy(); }} disabled={copyBusy}>
            {copyBusy ? 'Making copy…' : 'Download customer copy (PDF)'}
          </Button>
          {copyTask ? (
            <>
              <Button
                variant="ghost"
                onClick={() => { void recordCopyDelivery(COPY_HANDED); }}
                disabled={copyRecording}
              >
                Handed to customer
              </Button>
              <Button
                variant="ghost"
                onClick={() => { void recordCopyDelivery(COPY_MAILED); }}
                disabled={copyRecording}
              >
                Mailed to customer
              </Button>
            </>
          ) : null}
        </div>
        {copyTask?.notes && (
          <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>{copyTask.notes}</div>
        )}
      </div>

      {/* ── Follow-ups ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <SectionLabel text="Follow-ups" />
        {otherFollowUps.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            {otherFollowUps.map((t) => (
              <Fragment key={t.id}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-3)',
                  fontSize: 12,
                  color: 'var(--ink-700)',
                }}
              >
                <Pill variant="warn">Open task</Pill>
                <span>
                  {followUpLabel(t.taskCategory, t.payloadJson)}
                  {/* The CIP task says whose ID and which fields, and the maintenance e-statements task which
                      account (CHG-0045) — show it. */}
                  {(t.taskCategory === 'cip-core-entry'
                    || (t.taskCategory === 'estatement-verify' && (t.payloadJson ?? '').includes('maintenance-estatements')))
                    && t.notes ? ` — ${t.notes}` : ''}
                </span>
                {t.nextActionAt && (
                  <span style={{ color: 'var(--ink-500)' }}>
                    Next: {new Date(t.nextActionAt).toLocaleDateString()}
                  </span>
                )}
              </div>
              {(t.status === 'open' || t.status === 'in-progress') && (t.taskCategory === 'aurora-upload' || t.taskCategory === 'banno-enable') && (
                <div style={{ paddingLeft: 'var(--sp-5)' }}>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={doneBusy === t.id}
                    onClick={() => { void markDone(t.id, t.taskCategory === 'aurora-upload' ? 'Signed documents uploaded to Aurora' : 'Online banking enabled in Banno back office'); }}
                  >
                    {doneBusy === t.id ? 'Saving…' : 'Done'}
                  </Button>
                  {t.taskCategory === 'banno-enable' && (
                    <Button size="sm" variant="ghost" onClick={openBanno} style={{ marginLeft: 'var(--sp-3)' }}>
                      Open Banno back office
                    </Button>
                  )}
                  {t.taskCategory === 'banno-enable' && bannoNotice && (
                    <div role="status" style={{ marginTop: 'var(--sp-2)' }}>
                      <Banner tone="info">{BANNO_URL_NOT_SET_MESSAGE}</Banner>
                    </div>
                  )}
                </div>
              )}
              {(t.status === 'open' || t.status === 'in-progress') && t.taskCategory === 'remote-signature' && (
                <div style={{ paddingLeft: 'var(--sp-5)' }}>
                  <RemoteSignatureActions task={t} onChanged={() => { void refreshFollowUps(); }} />
                </div>
              )}
              {(t.status === 'open' || t.status === 'in-progress') && t.taskCategory === 'signature-outstanding' && (
                <div style={{ paddingLeft: 'var(--sp-5)' }}>
                  <SignatureOutstandingActions
                    task={t}
                    onChanged={() => { void refreshFollowUps(); }}
                    {...(onResumeSigning ? { onSignInBranch: onResumeSigning } : {})}
                  />
                </div>
              )}
              </Fragment>
            ))}
          </div>
        )}
        <ul
          style={{
            margin: 0,
            paddingLeft: 'var(--sp-7)',
            fontSize: 12,
            color: 'var(--ink-600)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
          }}
        >
          {NEXT_STEP_REMINDERS
            // The interactive card section supersedes the generic debit-card
            // reminder whenever the pipeline actually issued cards.
            .filter((r) => issuedCardCount === 0 || !r.startsWith('Debit card printing'))
            // BACKLOG-1747: Aurora filing is a tracked task now when the commit created one.
            .filter((r) => !r.startsWith('File the signed package in Aurora') || !otherFollowUps.some((t) => t.taskCategory === 'aurora-upload'))
            .map((r) => (
              <li key={r}>{r}</li>
            ))}
        </ul>
      </div>

      {auroraError && <Banner tone="err">{auroraError}</Banner>}

      {/* ── Actions ── */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-4)',
          justifyContent: 'flex-end',
          paddingTop: 'var(--sp-4)',
          borderTop: '1px solid var(--ink-200)',
        }}
      >
        <Button variant="ghost" onClick={() => { void handleAuroraDownload(); }} disabled={auroraBusy}>
          {auroraBusy ? 'Building package…' : 'Download for Aurora'}
        </Button>
        {onExit && (
          <Button variant="primary" onClick={onExit}>
            Back to applications
          </Button>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function SectionLabel({ text }: { text: string }): JSX.Element {
  return (
    <span
      style={{
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        fontWeight: 600,
        color: 'var(--ink-500)',
      }}
    >
      {text}
    </span>
  );
}

function MutedNote({ text }: { text: string }): JSX.Element {
  return <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>{text}</div>;
}

/** Core Director account-type letters → plain words (D=Checking ledger, S=Savings, T=CD). */
function humanizeServiceCode(code: string): string {
  const c = code.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (c.includes('debit')) return 'Debit card';
  if (c.includes('check')) return 'Check order';
  if (c.includes('stmt') || c.includes('statement')) return 'e-Statements';
  // PCB's online banking product IS NetTeller (Jack Henry) — Bill's word, and the term the
  // FSR and customer recognise. "Internet banking" was generic (Bill 2026-07-17: "I do not
  // see any information display regarding NetTeller Account creation and relationships").
  if (c.includes('internet') || c.includes('nettel') || c === 'ib') return 'NetTeller';
  if (c.includes('overdraft') || c.includes('od')) return 'Overdraft protection';
  return code;
}

function humanizeAcctType(t: string): string {
  switch (t.trim().toUpperCase()) {
    case 'D': return 'Checking (D)';
    case 'S': return 'Savings (S)';
    case 'T': return 'CD (T)';
    default: return t;
  }
}

function formatWhen(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
